import React, { useEffect, useMemo, useState } from 'react';
import Modal from './ui/Modal';
import { useTranslation } from '../hooks/useTranslation';

export interface MissingSpecBreakdown {
  specialization: string;
  shotCount: number;
  signals: string[];
  totalWeight: number;
}

export type SpecResolutionAction = 'create' | 'ignore' | 'redistribute';

export interface SpecResolutionEntry {
  action: SpecResolutionAction;
  targets?: Record<string, number>;
}

export type SpecResolutionMap = Record<string, SpecResolutionEntry>;

interface SpecRowState {
  action: SpecResolutionAction;
  targets: Record<string, number>;
}

interface AiSpecResolutionModalProps {
  isOpen: boolean;
  onClose: () => void;
  breakdown: MissingSpecBreakdown[];
  existingSpecializations: string[];
  onConfirm: (resolution: SpecResolutionMap) => void;
}

function defaultTargets(existing: string[]): Record<string, number> {
  const targets: Record<string, number> = {};
  if (!existing.length) return targets;
  const comp = existing.find((s) => s.toLowerCase() === 'comp');
  if (comp) {
    targets[comp] = 100;
    return targets;
  }
  targets[existing[0]] = 100;
  return targets;
}

function evenTargets(existing: string[]): Record<string, number> {
  if (!existing.length) return {};
  const share = Math.round((100 / existing.length) * 100) / 100;
  const targets: Record<string, number> = {};
  existing.forEach((s, i) => {
    if (i === existing.length - 1) {
      targets[s] = Math.round((100 - share * (existing.length - 1)) * 100) / 100;
    } else {
      targets[s] = share;
    }
  });
  return targets;
}

const AiSpecResolutionModal: React.FC<AiSpecResolutionModalProps> = ({
  isOpen,
  onClose,
  breakdown,
  existingSpecializations,
  onConfirm,
}) => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<Record<string, SpecRowState>>({});

  useEffect(() => {
    if (!isOpen) return;
    const initial: Record<string, SpecRowState> = {};
    for (const item of breakdown) {
      initial[item.specialization] = {
        action: 'ignore',
        targets: defaultTargets(existingSpecializations),
      };
    }
    setRows(initial);
  }, [isOpen, breakdown, existingSpecializations]);

  const validationError = useMemo(() => {
    for (const item of breakdown) {
      const row = rows[item.specialization];
      if (!row || row.action !== 'redistribute') continue;
      const sum = Object.values(row.targets).reduce((a, b) => a + b, 0);
      if (Math.abs(sum - 100) > 0.5) {
        return t('bidding.ai.specResolutionWeightError') || 'Weights must sum to 100%';
      }
      const hasPositive = Object.values(row.targets).some((v) => v > 0);
      if (!hasPositive) {
        return t('bidding.ai.specResolutionWeightError') || 'Weights must sum to 100%';
      }
    }
    return null;
  }, [breakdown, rows, t]);

  const setAction = (spec: string, action: SpecResolutionAction) => {
    setRows((prev) => ({
      ...prev,
      [spec]: {
        ...prev[spec],
        action,
        targets: prev[spec]?.targets || defaultTargets(existingSpecializations),
      },
    }));
  };

  const setTarget = (spec: string, targetSpec: string, value: number) => {
    setRows((prev) => ({
      ...prev,
      [spec]: {
        ...prev[spec],
        targets: {
          ...(prev[spec]?.targets || {}),
          [targetSpec]: value,
        },
      },
    }));
  };

  const applyEven = (spec: string) => {
    setRows((prev) => ({
      ...prev,
      [spec]: {
        ...prev[spec],
        targets: evenTargets(existingSpecializations),
      },
    }));
  };

  const handleConfirm = () => {
    if (validationError) return;
    const resolution: SpecResolutionMap = {};
    for (const item of breakdown) {
      const row = rows[item.specialization];
      if (!row) continue;
      if (row.action === 'redistribute') {
        resolution[item.specialization] = {
          action: 'redistribute',
          targets: Object.fromEntries(
            Object.entries(row.targets)
              .filter(([, w]) => w > 0)
              .map(([k, w]) => [k, w / 100])
          ),
        };
      } else {
        resolution[item.specialization] = { action: row.action };
      }
    }
    onConfirm(resolution);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('bidding.ai.specResolutionTitle') || 'Detected work types'}
      size="lg"
    >
      <p className="text-sm text-gray-400 mb-4">
        {t('bidding.ai.specResolutionHint') ||
          'AI detected disciplines that are not on your sheet. Choose what to do for each.'}
      </p>

      <div className="space-y-4">
        {breakdown.map((item) => {
          const row = rows[item.specialization];
          if (!row) return null;
          const targetSum = Object.values(row.targets).reduce((a, b) => a + b, 0);

          return (
            <div
              key={item.specialization}
              className="border border-gray-700 rounded-lg p-4 bg-gray-900/50"
            >
              <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
                <div>
                  <h4 className="text-white font-medium">{item.specialization}</h4>
                  <p className="text-xs text-gray-500 mt-1">
                    {t('bidding.ai.specResolutionShots') || 'Shots'}: {item.shotCount}
                    {item.signals.length > 0 && (
                      <span className="ml-2">
                        ({item.signals.join(', ')})
                      </span>
                    )}
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap gap-3 mb-3">
                {(['create', 'redistribute', 'ignore'] as SpecResolutionAction[]).map((action) => (
                  <label key={action} className="flex items-center gap-1.5 text-sm text-gray-300">
                    <input
                      type="radio"
                      name={`action-${item.specialization}`}
                      checked={row.action === action}
                      onChange={() => setAction(item.specialization, action)}
                    />
                    {action === 'create' &&
                      (t('bidding.ai.specResolutionCreate') || 'Create column')}
                    {action === 'redistribute' &&
                      (t('bidding.ai.specResolutionRedistribute') || 'Redistribute')}
                    {action === 'ignore' &&
                      (t('bidding.ai.specResolutionIgnore') || 'Ignore')}
                  </label>
                ))}
              </div>

              {row.action === 'redistribute' && (
                <div className="space-y-2 pl-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-gray-500">
                      {t('bidding.ai.specResolutionWeights') || 'Weight mapping (%)'}
                    </span>
                    <button
                      type="button"
                      onClick={() => applyEven(item.specialization)}
                      className="text-xs text-indigo-400 hover:text-indigo-300"
                    >
                      {t('bidding.ai.specResolutionEven') || 'Even split'}
                    </button>
                  </div>
                  {existingSpecializations.map((targetSpec) => (
                    <div key={targetSpec} className="flex items-center gap-2">
                      <label className="w-28 text-sm text-gray-300 shrink-0">{targetSpec}</label>
                      <input
                        type="number"
                        min={0}
                        max={100}
                        step={1}
                        value={row.targets[targetSpec] ?? 0}
                        onChange={(e) =>
                          setTarget(item.specialization, targetSpec, Number(e.target.value) || 0)
                        }
                        className="w-20 px-2 py-1 bg-gray-800 border border-gray-600 rounded text-white text-sm"
                      />
                      <span className="text-xs text-gray-500">%</span>
                    </div>
                  ))}
                  <p
                    className={`text-xs ${
                      Math.abs(targetSum - 100) <= 0.5 ? 'text-gray-500' : 'text-amber-400'
                    }`}
                  >
                    {t('bidding.ai.specResolutionSum') || 'Sum'}: {targetSum.toFixed(0)}%
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {validationError && (
        <p className="text-sm text-amber-400 mt-4">{validationError}</p>
      )}

      <div className="flex justify-end gap-2 mt-6">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded text-sm text-white"
        >
          {t('common.cancel') || 'Cancel'}
        </button>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={!!validationError}
          className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 rounded text-sm text-white disabled:opacity-50"
        >
          {t('bidding.ai.specResolutionContinue') || 'Continue allocate'}
        </button>
      </div>
    </Modal>
  );
};

export default AiSpecResolutionModal;
